import { useQueryTemplateById } from '@/hooks/query/useQueryTemplateById';
import { useActiveTabData } from '@/hooks/useActiveTabData';
import { getNoteTabTitle } from '@/utils/noteTabTitle';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { TabType } from '@heidi/session/contracts';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { useActiveArtifact } from '@/container/session-page/v2/session-panel/note-context-card/use-active-artifact';

import { voiceActiveTabAtom } from '@heidi/agents';

/**
 * Keeps Talk to Heidi aware of the active tab's doc/template.
 * Mount once under GlobalVoiceAssistant (feature-flag gated).
 */
export const useSyncVoiceActiveTab = () => {
  const sessionId = useCurrentSessionId();
  const { tabName, documentId } = useActiveTabData(sessionId ?? '');
  const artifact = useActiveArtifact();
  const setSnapshot = useSetAtom(voiceActiveTabAtom);

  const templateId = artifact?.templateId ?? undefined;
  const { data: template } = useQueryTemplateById(
    templateId ?? null,
    artifact?.kind === 'note' ? artifact.generationMethod : null
  );

  useEffect(() => {
    if (!sessionId) {
      setSnapshot(null);
      return;
    }

    const tabKind =
      tabName === TabType.NOTE
        ? 'note'
        : tabName === TabType.DOCUMENT
          ? 'document'
          : tabName === TabType.TRANSCRIPT
            ? 'transcript'
            : tabName === TabType.CONTEXT
              ? 'context'
              : String(tabName ?? 'unknown');

    const templateName =
      (artifact?.kind === 'note'
        ? getNoteTabTitle({
            generationMethod: artifact.generationMethod ?? undefined,
            currentTemplate: template ?? null,
            aiCommandText: artifact.aiCommandText ?? undefined,
            autoModeLabel: 'Auto',
            fallbackLabel: template?.name ?? undefined,
          })
        : null) ??
      template?.name ??
      (artifact?.kind === 'document' ? (artifact.fallbackName ?? undefined) : undefined);

    setSnapshot({
      sessionId,
      tabKind,
      ...(documentId ? { documentId } : {}),
      ...(templateId ? { templateId } : {}),
      ...(templateName ? { templateName } : {}),
      ...(artifact?.voiceStyle ? { voiceStyle: String(artifact.voiceStyle) } : {}),
    });
  }, [sessionId, tabName, documentId, templateId, template, artifact, setSnapshot]);

  useEffect(
    () => () => {
      setSnapshot(null);
    },
    [setSnapshot]
  );
};
